import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Database, PlayCircle, CheckCircle, Loader2, AlertTriangle, Layers } from "lucide-react";

/**
 * Self-contained admin data tools. Currently exposes the Platform Matrix
 * consolidation migration (legacy flat fields → canonical `platform_status`),
 * with a safe dry-run preview before the user confirms the write.
 */
export default function AdminMigrationTools() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run summary
  const [result, setResult] = useState(null); // executed summary

  const runDryRun = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/api/admin/migrate-platform-status", { confirm: false });
      setPreview(r.data);
      if ((r.data?.videos_to_update || 0) === 0) {
        toast.info("Nothing to migrate — platform data is already consolidated.");
      } else {
        toast.success(`Preview ready: ${r.data.videos_to_update} video(s) will be updated.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!window.confirm("Consolidate platform data now? This writes to your video catalog. It is safe and idempotent, but please make sure you have run the preview first.")) return;
    setBusy(true);
    try {
      const r = await api.post("/api/admin/migrate-platform-status", { confirm: true });
      setResult(r.data);
      setPreview(null);
      toast.success(`Done — ${r.data.updated ?? r.data.videos_to_update ?? 0} video(s) consolidated.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-migration-tools">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-2">
          <Database size={20} className="text-pink-500" />
          <h2 className="text-xl font-semibold">Consolidate Platform Data</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-5 max-w-2xl">
          Merge legacy per-platform fields (e.g. <code className="text-zinc-300">faphouse_status</code>,{" "}
          <code className="text-zinc-300">of_vip_posted_at</code>) into the single canonical{" "}
          <code className="text-zinc-300">platform_status</code> matrix used everywhere in the app. Run the
          preview first, then confirm. The migration is <strong>idempotent</strong> — running it again never
          double-writes.
        </p>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={runDryRun}
            disabled={busy}
            data-testid="migration-dryrun-btn"
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-white/10 rounded text-sm font-medium disabled:opacity-50 transition inline-flex items-center gap-2"
          >
            {busy && !result ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />}
            Preview (dry run)
          </button>
          <button
            onClick={runConfirm}
            disabled={busy || !preview || (preview?.videos_to_update || 0) === 0}
            data-testid="migration-confirm-btn"
            className="px-4 py-2 bg-pink-600 hover:bg-pink-700 rounded text-sm font-medium disabled:opacity-40 transition inline-flex items-center gap-2"
          >
            <CheckCircle size={15} />
            Run consolidation
          </button>
        </div>

        {/* Dry-run preview */}
        {preview && (
          <div className="mt-5 rounded-lg bg-black/40 border border-pink-500/20 p-4" data-testid="migration-preview">
            <div className="flex items-center gap-2 text-pink-300 text-sm font-semibold mb-3">
              <Layers size={15} /> Dry-run preview
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Stat label="Videos scanned" value={preview.videos_seen} />
              <Stat label="Will be updated" value={preview.videos_to_update} accent="#ec4899" />
            </div>
            {Array.isArray(preview.plan) && preview.plan.length > 0 && (
              <div className="mt-4 max-h-56 overflow-auto rounded border border-white/5">
                <table className="w-full text-xs">
                  <thead className="text-zinc-500 sticky top-0 bg-zinc-900">
                    <tr>
                      <th className="text-left px-3 py-2">Video</th>
                      <th className="text-left px-3 py-2">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.plan.slice(0, 100).map((p, i) => (
                      <tr key={p.video_id || i} className="border-t border-white/5">
                        <td className="px-3 py-2 text-zinc-300 truncate max-w-[240px]">{p.title || p.video_id}</td>
                        <td className="px-3 py-2 text-zinc-400">{(p.changes || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(preview.videos_to_update || 0) === 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-400">
                <CheckCircle size={13} /> Already consolidated — nothing to do.
              </div>
            )}
          </div>
        )}

        {/* Executed result */}
        {result && (
          <div className="mt-5 rounded-lg bg-emerald-950/20 border border-emerald-700/30 p-4" data-testid="migration-result">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold mb-2">
              <CheckCircle size={15} /> Consolidation complete
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Stat label="Videos scanned" value={result.videos_seen} />
              <Stat label="Updated" value={result.updated ?? result.videos_to_update} accent="#22c55e" />
            </div>
          </div>
        )}

        <div className="mt-5 flex items-start gap-2 text-[11px] text-zinc-500">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          Changes apply to the PREVIEW environment. Press Deploy to publish them to giopilos.com.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "#a1a1aa" }) {
  return (
    <div className="rounded-lg bg-black/40 border border-white/5 px-3 py-2">
      <div className="text-[11px] text-zinc-500">{label}</div>
      <div className="text-xl font-bold" style={{ color: accent }}>{value ?? 0}</div>
    </div>
  );
}
